import { TeklifShareButton } from '../../components/hesap/TeklifShareButton';
import { useMessageTemplates, HESAP_TEMPLATES } from '../../lib/messageTemplates';
import { useHesapBranding } from '../../store/hesapBrandingStore';
import { tl } from '../../lib/whatsapp';
import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHesapAuth } from '../../store/hesapStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash2, FileText, Search, Download, Edit2, X, Save, Loader2,
  Building2, Upload, Image as ImageIcon, Calendar, Filter, CheckCircle2, Clock, XCircle
} from 'lucide-react';

const API = `${RUNTIME_BACKEND_URL}/api/hesap`;

const STATUS_META = {
  beklemede: { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500', icon: Clock },
  onaylandi: { label: 'Onaylandı', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500', icon: CheckCircle2 },
  reddedildi: { label: 'Reddedildi', color: 'bg-red-100 text-red-700', dot: 'bg-red-500', icon: XCircle },
};

const emptyItem = () => ({ name: '', unit: 'adet', qty: 1, price: 0, total: 0 });
const emptyForm = () => ({
  customer_id: '',
  customer_name: '', customer_phone: '', customer_address: '', customer_email: '',
  date: new Date().toISOString().slice(0, 10),
  valid_until: '',
  items: [emptyItem()],
  subtotal: 0, tax_rate: 20, tax_amount: 0, discount: 0, total: 0,
  notes: '', status: 'beklemede'
});

export default function HesapTeklifPage() {
  const { token } = useHesapAuth();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [showFirma, setShowFirma] = useState(false);
  const brand = useHesapBranding(st => st.title);
  const tpl = useMessageTemplates('/api/hesap', token, HESAP_TEMPLATES);
  const [firma, setFirma] = useState({ name: '', phone: '', address: '', email: '', tax_no: '', iban: '', logo_data: '', footer_note: '' });
  const [firmaSaving, setFirmaSaving] = useState(false);

  // Autocomplete data - customers loaded once; products searched server-side on each keystroke
  const [customers, setCustomers] = useState([]);
  const [productResults, setProductResults] = useState({}); // { itemIndex: [products...] }
  const [productSearching, setProductSearching] = useState({}); // { itemIndex: bool }
  const prodSearchTimers = useRef({});

  // Autocomplete UI state
  const [custSuggestOpen, setCustSuggestOpen] = useState(false);
  const [prodSuggestIdx, setProdSuggestIdx] = useState(-1); // hangi item index'i açık
  const custBlurTimer = useRef(null);
  const prodBlurTimer = useRef(null);

  useEffect(() => {
    load();
    loadFirma();
    loadAutocompleteData();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const r = await fetch(`${API}/teklif?${params.toString()}`, { headers });
      if (r.ok) setList(await r.json());
    } finally { setLoading(false); }
  };

  const loadFirma = async () => {
    try {
      const r = await fetch(`${API}/firma-bilgi`, { headers });
      if (r.ok) setFirma(await r.json());
    } catch (_) {}
  };

  const loadAutocompleteData = async () => {
    try {
      const cr = await fetch(`${API}/customers`, { headers });
      if (cr.ok) setCustomers(await cr.json());
    } catch (_) {}
  };

  // Server-side product search on each keystroke (debounced)
  const searchProducts = (idx, query) => {
    // Önceki timer'ı iptal et
    if (prodSearchTimers.current[idx]) clearTimeout(prodSearchTimers.current[idx]);
    const q = (query || '').trim();
    if (!q) {
      setProductResults(prev => ({ ...prev, [idx]: [] }));
      setProductSearching(prev => ({ ...prev, [idx]: false }));
      return;
    }
    setProductSearching(prev => ({ ...prev, [idx]: true }));
    prodSearchTimers.current[idx] = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/products/search?q=${encodeURIComponent(q)}`, { headers });
        if (r.ok) {
          const data = await r.json();
          setProductResults(prev => ({ ...prev, [idx]: data.slice(0, 8) }));
        } else {
          setProductResults(prev => ({ ...prev, [idx]: [] }));
        }
      } catch (_) {
        setProductResults(prev => ({ ...prev, [idx]: [] }));
      } finally {
        setProductSearching(prev => ({ ...prev, [idx]: false }));
      }
    }, 220);
  };

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowEdit(true);
  };

  const openEdit = async (row) => {
    try {
      const r = await fetch(`${API}/teklif/${row.id}`, { headers });
      if (!r.ok) throw new Error('Yüklenemedi');
      const t = await r.json();
      setEditing(t);
      setForm({
        customer_id: t.customer_id || '',
        customer_name: t.customer_name || '',
        customer_phone: t.customer_phone || '',
        customer_address: t.customer_address || '',
        customer_email: t.customer_email || '',
        date: t.date || new Date().toISOString().slice(0, 10),
        valid_until: t.valid_until || '',
        items: (t.items && t.items.length ? t.items : [emptyItem()]).map(it => ({
          name: it.name || '', unit: it.unit || 'adet',
          qty: Number(it.qty || 0), price: Number(it.price || 0), total: Number(it.total || 0),
        })),
        subtotal: Number(t.subtotal || 0),
        tax_rate: Number(t.tax_rate || 0),
        tax_amount: Number(t.tax_amount || 0),
        discount: Number(t.discount || 0),
        total: Number(t.total || 0),
        notes: t.notes || '',
        status: t.status || 'beklemede',
      });
      setShowEdit(true);
    } catch (e) { toast.error(e.message); }
  };

  // Otomatik hesap - form değiştikçe subtotal / tax / total
  useEffect(() => {
    let subtotal = 0;
    const newItems = form.items.map(it => {
      const total = Number(it.qty || 0) * Number(it.price || 0);
      subtotal += total;
      return { ...it, total: Math.round(total * 100) / 100 };
    });
    const discount = Number(form.discount || 0);
    const afterDiscount = Math.max(0, subtotal - discount);
    const tax_amount = Math.round((afterDiscount * Number(form.tax_rate || 0)) / 100 * 100) / 100;
    const total = Math.round((afterDiscount + tax_amount) * 100) / 100;
    const changed =
      subtotal !== form.subtotal ||
      tax_amount !== form.tax_amount ||
      total !== form.total ||
      newItems.some((it, i) => it.total !== form.items[i]?.total);
    if (changed) {
      setForm(f => ({ ...f, items: newItems, subtotal: Math.round(subtotal * 100) / 100, tax_amount, total }));
    }
    // eslint-disable-next-line
  }, [form.items.map(i => `${i.qty}|${i.price}`).join(','), form.tax_rate, form.discount]);

  const updateItem = (idx, key, val) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === idx ? { ...it, [key]: val } : it)
    }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.customer_name.trim()) { toast.error('Müşteri adı zorunlu'); return; }
    if (!form.items.length || form.items.every(i => !i.name.trim())) { toast.error('En az bir ürün/hizmet girin'); return; }
    setSaving(true);
    try {
      const body = { ...form, items: form.items.filter(i => i.name.trim()) };
      const url = editing ? `${API}/teklif/${editing.id}` : `${API}/teklif`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Kaydedilemedi');
      }
      toast.success(editing ? 'Teklif güncellendi' : 'Teklif kaydedildi');
      setShowEdit(false);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const changeStatus = async (row, newStatus) => {
    try {
      const r = await fetch(`${API}/teklif/${row.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ status: newStatus })
      });
      if (!r.ok) throw new Error('Güncellenemedi');
      toast.success('Durum güncellendi');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (row) => {
    if (!window.confirm(`"${row.teklif_no}" numaralı teklif silinsin mi?`)) return;
    const r = await fetch(`${API}/teklif/${row.id}`, { method: 'DELETE', headers });
    if (r.ok) { toast.success('Teklif silindi'); load(); }
    else toast.error('Silinemedi');
  };

  const downloadPdf = async (row) => {
    try {
      const r = await fetch(`${API}/teklif/${row.id}/pdf`, { headers });
      if (!r.ok) throw new Error('PDF alınamadı');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `teklif_${row.teklif_no}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  const onLogoFile = (file) => {
    if (!file) return;
    if (file.size > 800 * 1024) { toast.error('Logo 800KB\'dan küçük olmalı'); return; }
    const reader = new FileReader();
    reader.onload = () => setFirma(f => ({ ...f, logo_data: reader.result }));
    reader.readAsDataURL(file);
  };

  const saveFirma = async () => {
    setFirmaSaving(true);
    try {
      const r = await fetch(`${API}/firma-bilgi`, {
        method: 'PUT', headers, body: JSON.stringify(firma)
      });
      if (!r.ok) throw new Error('Kaydedilemedi');
      const d = await r.json();
      setFirma(d);
      toast.success('Firma bilgileri güncellendi');
      setShowFirma(false);
    } catch (e) { toast.error(e.message); }
    finally { setFirmaSaving(false); }
  };

  // ---- Autocomplete filters ----
  const customerSuggestions = useMemo(() => {
    const s = (form.customer_name || '').toLowerCase().trim();
    if (!s) return [];
    return customers.filter(c => {
      const nm = (c.full_name || c.name || '').toLowerCase();
      const ph = (c.phone || '').toLowerCase();
      return nm.includes(s) || ph.includes(s);
    }).slice(0, 8);
  }, [form.customer_name, customers]);

  const pickCustomer = (c) => {
    setForm(f => ({
      ...f,
      customer_id: c.id,
      customer_name: c.full_name || c.name || '',
      customer_phone: c.phone || f.customer_phone,
      customer_address: c.address || f.customer_address,
      customer_email: c.email || f.customer_email,
    }));
    setCustSuggestOpen(false);
  };

  const productSuggestionsFor = (idx) => productResults[idx] || [];

  const pickProduct = (idx, p) => {
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === idx ? {
        ...it,
        name: p.name || it.name,
        price: Number(p.sell_price || p.price || 0),
        unit: p.unit || it.unit || 'adet',
        qty: Number(it.qty || 1) || 1,
      } : it)
    }));
    setProdSuggestIdx(-1);
  };

  const filteredList = list.filter(x => !statusFilter || x.status === statusFilter);
  const totals = useMemo(() => ({
    count: filteredList.length,
    sum: filteredList.reduce((a, b) => a + (Number(b.total) || 0), 0),
  }), [filteredList]);

  return (
    <div className="space-y-4" data-testid="hesap-teklif-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <FileText className="h-6 w-6 text-emerald-600" />
            Teklif Formu
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Teklif oluştur, kaydet, PDF çıktısı al</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowFirma(true)} data-testid="firma-bilgi-btn">
            <Building2 className="h-4 w-4 mr-1" />Firma Bilgileri
          </Button>
          <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-teklif-btn">
            <Plus className="h-4 w-4 mr-1" />Yeni Teklif
          </Button>
        </div>
      </div>

      {/* Filtre */}
      <Card>
        <CardContent className="p-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
            <div className="md:col-span-2">
              <Label className="text-xs">Ara (müşteri / no / not)</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Arama..." className="pl-8" data-testid="teklif-search" />
              </div>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" />Başlangıç</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" />Bitiş</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button onClick={load} className="flex-1" data-testid="teklif-filter-btn">
                <Filter className="h-4 w-4 mr-1" />Filtrele
              </Button>
              <Button variant="outline" onClick={() => { setQ(''); setDateFrom(''); setDateTo(''); setStatusFilter(''); setTimeout(load, 50); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-1 flex-wrap">
              <button onClick={() => setStatusFilter('')}
                className={`text-xs px-2.5 py-1 rounded-full border transition ${!statusFilter ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                Tümü
              </button>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <button key={k} onClick={() => setStatusFilter(k)}
                  className={`text-xs px-2.5 py-1 rounded-full border flex items-center gap-1 transition ${statusFilter === k ? `${v.color} border-current font-semibold` : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${v.dot}`} />
                  {v.label}
                </button>
              ))}
            </div>
            <div className="text-xs text-slate-500">
              Toplam: <b>{totals.count}</b> teklif · Tutar: <b>{totals.sum.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</b>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Liste */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Teklif No</th>
                    <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Müşteri</th>
                    <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300 hidden md:table-cell">Telefon</th>
                    <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Tarih</th>
                    <th className="text-left p-3 font-medium text-slate-600 dark:text-slate-300">Durum</th>
                    <th className="text-right p-3 font-medium text-slate-600 dark:text-slate-300">Tutar</th>
                    <th className="text-right p-3 font-medium text-slate-600 dark:text-slate-300">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredList.map(row => {
                    const st = STATUS_META[row.status] || STATUS_META.beklemede;
                    return (
                      <tr key={row.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                        <td className="p-3 font-mono text-xs text-emerald-700 dark:text-emerald-400 font-semibold">{row.teklif_no}</td>
                        <td className="p-3 font-medium">{row.customer_name}</td>
                        <td className="p-3 text-slate-500 hidden md:table-cell">{row.customer_phone || '-'}</td>
                        <td className="p-3 text-slate-500 text-xs">
                          {row.date ? new Date(row.date).toLocaleDateString('tr-TR') : '-'}
                        </td>
                        <td className="p-3">
                          <select value={row.status || 'beklemede'} onChange={(e) => changeStatus(row, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className={`text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer focus:outline-none ${st.color}`}
                            data-testid={`teklif-status-${row.id}`}>
                            <option value="beklemede">Beklemede</option>
                            <option value="onaylandi">Onaylandı</option>
                            <option value="reddedildi">Reddedildi</option>
                          </select>
                        </td>
                        <td className="p-3 text-right font-semibold text-slate-800 dark:text-slate-200">
                          {Number(row.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1 items-center">
                            {row.customer_phone && (
                              <TeklifShareButton row={row} token={token} template={tpl.get('teklif')} firma={firma.name || brand} />
                            )}
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => downloadPdf(row)} title="PDF" data-testid={`teklif-pdf-${row.id}`}>
                              <Download className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(row)} title="Düzenle" data-testid={`teklif-edit-${row.id}`}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(row)} title="Sil" data-testid={`teklif-delete-${row.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredList.length === 0 && (
                    <tr><td colSpan={7} className="p-8 text-center text-slate-400">
                      Kayıtlı teklif yok. <button className="text-emerald-600 underline" onClick={openNew}>Yeni teklif oluştur</button>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Yeni/Düzenle Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              {editing ? `Teklifi Düzenle · ${editing.teklif_no}` : 'Yeni Teklif'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Müşteri & Tarih */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative">
                <Label>Müşteri Adı *</Label>
                <Input
                  value={form.customer_name}
                  onChange={e => {
                    setForm({ ...form, customer_name: e.target.value, customer_id: '' });
                    setCustSuggestOpen(true);
                  }}
                  onFocus={() => setCustSuggestOpen(true)}
                  onBlur={() => { custBlurTimer.current = setTimeout(() => setCustSuggestOpen(false), 180); }}
                  placeholder="Yazmaya başlayın..."
                  data-testid="teklif-customer-name"
                  autoComplete="off"
                />
                {custSuggestOpen && customerSuggestions.length > 0 && (
                  <div className="absolute z-30 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-64 overflow-y-auto">
                    {customerSuggestions.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={() => { clearTimeout(custBlurTimer.current); pickCustomer(c); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border-b border-slate-100 dark:border-slate-700 last:border-0"
                        data-testid={`teklif-customer-sug-${c.id}`}
                      >
                        <div className="font-medium text-slate-800 dark:text-slate-100">{c.full_name || c.name}</div>
                        <div className="text-xs text-slate-500">
                          {c.phone || '—'} {c.total_debt > 0 && <span className="text-red-600 ml-2">Borç: {Number(c.total_debt).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {form.customer_id && (
                  <div className="text-[10px] text-emerald-600 mt-0.5 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Veresiye müşterisiyle eşleştirildi
                  </div>
                )}
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Adres</Label>
                <Input value={form.customer_address} onChange={e => setForm({ ...form, customer_address: e.target.value })} />
              </div>
              <div>
                <Label>E-posta</Label>
                <Input type="email" value={form.customer_email} onChange={e => setForm({ ...form, customer_email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Teklif Tarihi</Label>
                  <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
                </div>
                <div>
                  <Label>Geçerlilik</Label>
                  <Input type="date" value={form.valid_until} onChange={e => setForm({ ...form, valid_until: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Durum</Label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                  <option value="beklemede">Beklemede</option>
                  <option value="onaylandi">Onaylandı</option>
                  <option value="reddedildi">Reddedildi</option>
                </select>
              </div>
            </div>

            {/* Ürün Kalemleri */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Kalemler</Label>
                <Button size="sm" variant="outline" onClick={addItem} data-testid="teklif-add-item">
                  <Plus className="h-4 w-4 mr-1" />Kalem Ekle
                </Button>
              </div>
              <div className="border rounded-lg overflow-visible">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-800">
                    <tr>
                      <th className="p-2 text-left rounded-tl-lg">Açıklama (ürün ara)</th>
                      <th className="p-2 text-left w-20 sm:w-24">Birim</th>
                      <th className="p-2 text-right w-20">Miktar</th>
                      <th className="p-2 text-right w-24 sm:w-28">Br. Fiyat</th>
                      <th className="p-2 text-right w-24 sm:w-28">Tutar</th>
                      <th className="p-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((it, i) => {
                      const prodSugs = prodSuggestIdx === i ? productSuggestionsFor(i) : [];
                      const isSearching = prodSuggestIdx === i && productSearching[i];
                      const hasQuery = prodSuggestIdx === i && (it.name || '').trim().length > 0;
                      const showPanel = prodSuggestIdx === i && hasQuery && (prodSugs.length > 0 || isSearching || !isSearching);
                      return (
                        <tr key={i} className="border-t">
                          <td className="p-1 relative">
                            <Input
                              value={it.name}
                              onChange={e => { updateItem(i, 'name', e.target.value); setProdSuggestIdx(i); searchProducts(i, e.target.value); }}
                              onFocus={() => { setProdSuggestIdx(i); if ((it.name || '').trim()) searchProducts(i, it.name); }}
                              onBlur={() => { prodBlurTimer.current = setTimeout(() => setProdSuggestIdx(-1), 180); }}
                              placeholder="Ürün / hizmet adı"
                              className="h-8 text-xs"
                              autoComplete="off"
                              data-testid={`teklif-item-name-${i}`}
                            />
                            {showPanel && (
                              <div className="absolute z-40 left-0 right-0 top-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-56 overflow-y-auto">
                                {isSearching && prodSugs.length === 0 ? (
                                  <div className="px-3 py-2 text-xs text-slate-500 flex items-center gap-2">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Aranıyor...
                                  </div>
                                ) : prodSugs.length === 0 ? (
                                  <div className="px-3 py-2 text-xs text-slate-400 italic">
                                    "{it.name}" için ürün bulunamadı — serbest metin olarak devam edebilirsiniz.
                                  </div>
                                ) : prodSugs.map(p => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    onMouseDown={() => { clearTimeout(prodBlurTimer.current); pickProduct(i, p); }}
                                    className="w-full text-left px-3 py-2 text-xs hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border-b border-slate-100 dark:border-slate-700 last:border-0"
                                    data-testid={`teklif-prod-sug-${i}-${p.id}`}
                                  >
                                    <div className="font-medium text-slate-800 dark:text-slate-100">{p.name}</div>
                                    <div className="text-[11px] text-slate-500 flex items-center gap-3">
                                      <span className="text-emerald-600 font-semibold">{Number(p.sell_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span>
                                      {p.stock !== undefined && <span>Stok: {p.stock}</span>}
                                      {p.barcode && <span className="font-mono">{p.barcode}</span>}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="p-1">
                            <Input value={it.unit} onChange={e => updateItem(i, 'unit', e.target.value)}
                              placeholder="adet" className="h-8 text-xs" />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="0.01" value={it.qty}
                              onChange={e => updateItem(i, 'qty', e.target.value)}
                              className="h-8 text-xs text-right" />
                          </td>
                          <td className="p-1">
                            <Input type="number" step="0.01" value={it.price}
                              onChange={e => updateItem(i, 'price', e.target.value)}
                              className="h-8 text-xs text-right" />
                          </td>
                          <td className="p-2 text-right font-medium">
                            {Number(it.total || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="p-1 text-center">
                            {form.items.length > 1 && (
                              <button onClick={() => removeItem(i)} className="text-red-500 hover:text-red-700">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">İpucu: Açıklama kısmına ürün adı yazmaya başlayın — mevcut ürünleriniz otomatik listelenir.</p>
            </div>

            {/* Toplamlar */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Notlar</Label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  className="w-full h-28 rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="İsteğe bağlı notlar..." />
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-300">Ara Toplam</span>
                  <b>{Number(form.subtotal).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</b>
                </div>
                <div className="flex justify-between items-center text-sm gap-2">
                  <span className="text-slate-600 dark:text-slate-300">İndirim (TL)</span>
                  <Input type="number" step="0.01" value={form.discount}
                    onChange={e => setForm({ ...form, discount: Number(e.target.value) })}
                    className="h-8 text-xs text-right w-28" />
                </div>
                <div className="flex justify-between items-center text-sm gap-2">
                  <span className="text-slate-600 dark:text-slate-300">KDV %</span>
                  <Input type="number" step="0.01" value={form.tax_rate}
                    onChange={e => setForm({ ...form, tax_rate: Number(e.target.value) })}
                    className="h-8 text-xs text-right w-28" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600 dark:text-slate-300">KDV Tutarı</span>
                  <b>{Number(form.tax_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</b>
                </div>
                <div className="flex justify-between text-base pt-2 border-t border-slate-300 dark:border-slate-700">
                  <span className="font-bold">GENEL TOPLAM</span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400 text-lg">
                    {Number(form.total).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowEdit(false)}>İptal</Button>
              <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="teklif-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Kaydet</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Firma Bilgileri Dialog */}
      <Dialog open={showFirma} onOpenChange={setShowFirma}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-600" />Firma Bilgileri (Teklif Başlığı)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Logo</Label>
              <div className="flex items-center gap-3 mt-1">
                {firma.logo_data ? (
                  <img src={firma.logo_data} alt="logo" className="h-16 w-16 object-contain rounded border bg-white p-1" />
                ) : (
                  <div className="h-16 w-16 rounded border border-dashed flex items-center justify-center text-slate-400">
                    <ImageIcon className="h-6 w-6" />
                  </div>
                )}
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={e => onLogoFile(e.target.files?.[0])} data-testid="firma-logo-input" />
                  <span className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 px-3 py-2 rounded-md">
                    <Upload className="h-4 w-4" />Logo Yükle
                  </span>
                </label>
                {firma.logo_data && (
                  <button className="text-xs text-red-500" onClick={() => setFirma({ ...firma, logo_data: '' })}>Kaldır</button>
                )}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">PNG/JPG, maks 800KB</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Firma Adı</Label>
                <Input value={firma.name || ''} onChange={e => setFirma({ ...firma, name: e.target.value })} data-testid="firma-name" />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input value={firma.phone || ''} onChange={e => setFirma({ ...firma, phone: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Adres</Label>
                <Input value={firma.address || ''} onChange={e => setFirma({ ...firma, address: e.target.value })} />
              </div>
              <div>
                <Label>E-posta</Label>
                <Input value={firma.email || ''} onChange={e => setFirma({ ...firma, email: e.target.value })} />
              </div>
              <div>
                <Label>Vergi No</Label>
                <Input value={firma.tax_no || ''} onChange={e => setFirma({ ...firma, tax_no: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>IBAN</Label>
                <Input value={firma.iban || ''} onChange={e => setFirma({ ...firma, iban: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Alt Not (PDF Altına)</Label>
                <Input value={firma.footer_note || ''} onChange={e => setFirma({ ...firma, footer_note: e.target.value })}
                  placeholder="Teklifimiz hakkında..." />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowFirma(false)}>İptal</Button>
              <Button onClick={saveFirma} disabled={firmaSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="firma-save-btn">
                {firmaSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />Kaydet</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
